'use client';

import { AlertCircle, RefreshCw04, SearchLg, XClose } from '@untitledui/icons';
import type { Key, KeyboardEvent, ReactNode, RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FocusScope, useFocusManager } from 'react-aria';
import {
  ComboBox as AriaComboBox,
  Group as AriaGroup,
  Header as AriaHeader,
  Input as AriaInput,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxSection as AriaSection,
} from 'react-aria-components';

import { useResizeObserver } from '@/hooks/useResizeObserver';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Badge } from '@/lib/base/badges/badges';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Popover } from '@/lib/base/overlays/popover';
import { isPersonType } from '@/lib/crm/core/entityTypes';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import {
  addRecentSearch,
  ENTITY_TYPE_TO_RESULT_KEY,
  type EntityResult,
  type EntityType,
  getRecentSearches,
  type SearchContext,
  useEntitySearch,
} from '@/lib/search';
import { cx } from '@/utils/cx';

export interface EntityTypeaheadProps {
  value?: EntityResult | EntityResult[];
  onChange: (value: EntityResult | EntityResult[] | null) => void;
  onSelect?: (entity: EntityResult) => void;
  multiple?: boolean;
  types?: EntityType[];
  context?: SearchContext;
  placeholder?: string;
  disabled?: boolean;
  orgId: number;
  className?: string;
  /**
   * Caller-provided options shown ABOVE the CRM search results in their own
   * section — e.g. entities being created elsewhere in a proposal that don't
   * exist in the CRM yet. They render with a "New" badge, are filtered by the
   * typed query, are selectable like search results, and are NOT written to the
   * recent-searches history.
   */
  extraOptions?: EntityResult[];
  /** Section header for `extraOptions`. Defaults to "In this action". */
  extraOptionsLabel?: string;
}

type GroupedResults = {
  users: EntityResult[];
  contacts: EntityResult[];
  companies: EntityResult[];
  deals: EntityResult[];
};

const MIN_QUERY_LENGTH = 2;

// react-aria-components applies a built-in "contains" filter to a ComboBox whose
// collection is built from static children, matching each item's textValue against
// inputValue. EntityTypeahead's results are already filtered by the server
// (useEntitySearch) and its status rows ("No results", "Searching…", error, "Type at
// least 2 characters") have query-independent textValues — so react-aria's second
// client-side filter silently hides rows. Keeping every item disables that filter
// and lets the component render exactly what it decides to render.
const KEEP_ALL_ITEMS = (): boolean => true;

/**
 * Get the display label for an entity type section
 */
function getSectionLabel(type: EntityType): string {
  switch (type) {
    case 'ahoy_user':
      return 'Users';
    case 'ahoy_contact':
      return 'Contacts';
    case 'ahoy_company':
      return 'Companies';
    case 'ahoy_deal':
      return 'Deals';
    default:
      return type;
  }
}

/**
 * Group results by entity type
 */
function groupResults(
  results: {
    users?: EntityResult[];
    contacts: EntityResult[];
    companies: EntityResult[];
    deals: EntityResult[];
  } | null
): GroupedResults {
  if (!results) {
    return { users: [], contacts: [], companies: [], deals: [] };
  }
  return {
    users: results.users || [],
    contacts: results.contacts || [],
    companies: results.companies || [],
    deals: results.deals || [],
  };
}

/**
 * Check if there are any results
 */
function hasResults(grouped: GroupedResults): boolean {
  return (
    grouped.users.length > 0 ||
    grouped.contacts.length > 0 ||
    grouped.companies.length > 0 ||
    grouped.deals.length > 0
  );
}

/**
 * Leading avatar for an entity result: the profile/brand image when present,
 * initials for people without a photo, and the entity-type icon for everything
 * else. Mirrors the image → initials → type-icon fallback the entity badges use.
 */
function EntityResultAvatar({
  entity,
  size,
  iconClassName,
}: {
  entity: EntityResult;
  size: 'xxs' | 'xs';
  iconClassName: string;
}) {
  if (entity.avatar_url) {
    return <Avatar size={size} src={entity.avatar_url} alt={entity.name} />;
  }
  if (isPersonType(entity.type)) {
    return (
      <Avatar
        size={size}
        alt={entity.name}
        initials={getAvatarInitials(entity.name)}
      />
    );
  }
  const Icon = getEntityTypeIconComponent(entity.type);
  return <Icon className={iconClassName} aria-hidden />;
}

/**
 * A single selectable row in the dropdown (recents, search results, or extra
 * options). `badge` renders a trailing marker (e.g. "New" for proposed
 * entities).
 */
function EntityOptionRow({
  entity,
  isFocused,
  isSelected,
  badge,
}: {
  entity: EntityResult;
  isFocused: boolean;
  isSelected: boolean;
  badge?: ReactNode;
}) {
  return (
    <div
      className={cx(
        'flex cursor-pointer items-center gap-2 rounded-md p-2 pr-2.5 outline-hidden select-none',
        isSelected && 'bg-active',
        isFocused && 'bg-primary_hover'
      )}
    >
      <EntityResultAvatar
        entity={entity}
        size='xs'
        iconClassName='size-6 text-fg-quaternary'
      />
      <div className='flex min-w-0 flex-1 flex-col'>
        <span className='truncate text-sm font-medium text-primary'>
          {entity.name}
        </span>
        {entity.secondary_text && (
          <span className='truncate text-xs text-tertiary'>
            {entity.secondary_text}
          </span>
        )}
      </div>
      {badge}
    </div>
  );
}

/**
 * Entity chip component for multi-select mode
 */
function EntityChip({
  entity,
  onRemove,
  disabled,
}: {
  entity: EntityResult;
  onRemove: (entity: EntityResult) => void;
  disabled?: boolean;
}) {
  return (
    <span className='flex items-center gap-1 rounded-md bg-primary py-0.5 pr-1 pl-1.5 ring-1 ring-primary ring-inset'>
      <EntityResultAvatar
        entity={entity}
        size='xxs'
        iconClassName='size-4 text-fg-quaternary'
      />
      <span className='truncate text-sm font-medium text-secondary max-w-32'>
        {entity.name}
      </span>
      {!disabled && (
        <button
          type='button'
          onClick={e => {
            e.stopPropagation();
            onRemove(entity);
          }}
          className='ml-0.5 rounded-sm p-0.5 hover:bg-tertiary focus:outline-none focus:ring-1 focus:ring-focus-ring'
          aria-label={`Remove ${entity.name}`}
        >
          <XClose className='size-3 text-fg-quaternary' />
        </button>
      )}
    </span>
  );
}

/**
 * Inner component that handles the input and chip rendering
 */
function InnerTypeahead({
  selectedItems,
  onRemoveItem,
  onInputChange,
  onKeyDown,
  inputValue,
  placeholder,
  disabled,
  multiple,
}: {
  selectedItems: EntityResult[];
  onRemoveItem: (entity: EntityResult) => void;
  onInputChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  inputValue: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
}) {
  const focusManager = useFocusManager();

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const isCaretAtStart =
      event.currentTarget.selectionStart === 0 &&
      event.currentTarget.selectionEnd === 0;

    if (!isCaretAtStart && event.currentTarget.value !== '') {
      onKeyDown(event);
      return;
    }

    switch (event.key) {
      case 'Backspace':
        if (multiple && selectedItems.length > 0 && inputValue === '') {
          // Remove last chip
          onRemoveItem(selectedItems[selectedItems.length - 1]);
        }
        break;
      case 'ArrowLeft':
        if (multiple && selectedItems.length > 0) {
          focusManager?.focusPrevious({ wrap: false, tabbable: false });
        }
        break;
      default:
        onKeyDown(event);
    }
  };

  const isSelectionEmpty = selectedItems.length === 0;

  const hasSingleSelection = !multiple && !isSelectionEmpty;

  return (
    <div className='relative flex w-full flex-1 flex-row flex-wrap items-center gap-1.5 max-h-24 overflow-y-auto'>
      {!isSelectionEmpty &&
        selectedItems.map(entity => (
          <EntityChip
            key={`${entity.type}-${entity.id}`}
            entity={entity}
            onRemove={onRemoveItem}
            disabled={disabled}
          />
        ))}

      <div
        className={cx(
          'relative flex min-w-[20%] flex-1 flex-row items-center',
          !isSelectionEmpty && 'ml-0.5',
          hasSingleSelection && 'sr-only'
        )}
      >
        <AriaInput
          placeholder={isSelectionEmpty ? placeholder : ''}
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          className='w-full flex-[1_0_0] appearance-none bg-transparent text-md text-ellipsis text-primary caret-alpha-black/90 outline-none placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled'
        />
      </div>
    </div>
  );
}

export function EntityTypeahead({
  value,
  onChange,
  onSelect,
  multiple = false,
  types,
  context = 'email',
  placeholder = 'Search users, contacts, companies, and deals',
  disabled = false,
  orgId,
  className,
  extraOptions,
  extraOptionsLabel = 'In this action',
}: EntityTypeaheadProps) {
  const [inputValue, setInputValue] = useState('');
  const [focusedSectionIndex, setFocusedSectionIndex] = useState(-1);

  const placeholderRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  // Normalize value to array for internal handling
  const selectedItems = useMemo(() => {
    if (!value) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Fetch search results
  const { results, isLoading, error, refetch } = useEntitySearch(inputValue, {
    types,
    enabled: inputValue.length >= MIN_QUERY_LENGTH,
  });

  // Get recent searches
  const recentSearches = useMemo(() => {
    if (inputValue.length > 0) {
      return [];
    }
    return getRecentSearches(orgId, context);
  }, [orgId, context, inputValue.length]);

  // Group results for display
  const groupedResults = useMemo(() => groupResults(results), [results]);

  // Caller-provided "extra" options (e.g. proposed entities in a proposal),
  // client-filtered by the typed query. Shown in their own section, before the
  // CRM search results / recents.
  const visibleExtras = useMemo(() => {
    const extras = extraOptions ?? [];
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      return extras;
    }
    return extras.filter(
      e =>
        e.name.toLowerCase().includes(query) ||
        (e.secondary_text?.toLowerCase().includes(query) ?? false)
    );
  }, [extraOptions, inputValue]);

  // Get ordered sections that have results
  const activeSections = useMemo(() => {
    const sections: { type: EntityType; items: EntityResult[] }[] = [];
    const order: EntityType[] = [
      'ahoy_user',
      'ahoy_contact',
      'ahoy_company',
      'ahoy_deal',
    ];

    for (const type of order) {
      const key = ENTITY_TYPE_TO_RESULT_KEY[type] as keyof GroupedResults;
      const items = key ? groupedResults[key] : [];
      if (items.length > 0) {
        sections.push({ type, items });
      }
    }

    return sections;
  }, [groupedResults]);

  // Memoized style object for the popover, so it doesn't get a new reference
  // on every render.
  const popoverStyle = useMemo(() => ({ width: popoverWidth }), [popoverWidth]);

  // Resize observer for popover width
  const onResize = useCallback(() => {
    if (!placeholderRef.current) {
      return;
    }
    const divRect = placeholderRef.current.getBoundingClientRect();
    setPopoverWidth(divRect.width + 'px');
  }, []);

  useResizeObserver({
    ref: placeholderRef,
    onResize,
    box: 'border-box',
  });

  // Handle selection
  const handleSelect = useCallback(
    (entity: EntityResult) => {
      // Add to recents — but never for extra options; they're proposed entities
      // that don't exist in the CRM, so they shouldn't leak into search history.
      const isExtra = (extraOptions ?? []).some(
        e => e.type === entity.type && e.id === entity.id
      );
      if (!isExtra) {
        addRecentSearch(orgId, context, entity);
      }

      if (multiple) {
        const newItems = [...selectedItems, entity];
        onChange(newItems);
      } else {
        onChange(entity);
        // ComboBox closes automatically in single-select mode via onSelectionChange
      }

      onSelect?.(entity);
      setInputValue('');
    },
    [multiple, selectedItems, onChange, onSelect, orgId, context, extraOptions]
  );

  // Handle removing an item
  const handleRemoveItem = useCallback(
    (entity: EntityResult) => {
      if (multiple) {
        const newItems = selectedItems.filter(
          item => !(item.type === entity.type && item.id === entity.id)
        );
        onChange(newItems.length > 0 ? newItems : null);
      } else {
        onChange(null);
      }
    },
    [multiple, selectedItems, onChange]
  );

  // Handle keyboard navigation for sections
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab' && !e.shiftKey && activeSections.length > 0) {
        e.preventDefault();
        // Jump to next section
        const nextIndex = (focusedSectionIndex + 1) % activeSections.length;
        setFocusedSectionIndex(nextIndex);
      }
    },
    [activeSections.length, focusedSectionIndex]
  );

  // Handle selection from listbox
  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      if (!key) {
        return;
      }

      const keyStr = key.toString();
      const [type, id] = keyStr.split(':');

      // Resolve the selected key against every rendered pool: extra options
      // first (they render at the top), then recents (empty query) or search
      // results.
      const pool: EntityResult[] = [
        ...visibleExtras,
        ...(inputValue.length === 0 && recentSearches.length > 0
          ? recentSearches
          : [
              ...groupedResults.users,
              ...groupedResults.contacts,
              ...groupedResults.companies,
              ...groupedResults.deals,
            ]),
      ];
      const entity = pool.find(e => e.type === type && e.id === id);

      if (entity) {
        handleSelect(entity);
      }
    },
    [
      inputValue.length,
      recentSearches,
      groupedResults,
      visibleExtras,
      handleSelect,
    ]
  );

  // Determine what to show in the dropdown
  const showQueryTooShort =
    inputValue.length > 0 &&
    inputValue.length < MIN_QUERY_LENGTH &&
    visibleExtras.length === 0;
  const showLoading = inputValue.length >= MIN_QUERY_LENGTH && isLoading;
  const showError =
    inputValue.length >= MIN_QUERY_LENGTH && error && !isLoading;
  const showNoResults =
    inputValue.length >= MIN_QUERY_LENGTH &&
    !isLoading &&
    !error &&
    results &&
    !hasResults(groupedResults) &&
    visibleExtras.length === 0;
  const showResults =
    inputValue.length >= MIN_QUERY_LENGTH &&
    !isLoading &&
    !error &&
    hasResults(groupedResults);
  const showRecents = inputValue.length === 0 && recentSearches.length > 0;
  const showEmptyRecents =
    inputValue.length === 0 &&
    recentSearches.length === 0 &&
    visibleExtras.length === 0;
  const showExtras = visibleExtras.length > 0;

  return (
    <AriaComboBox
      allowsEmptyCollection
      defaultFilter={KEEP_ALL_ITEMS}
      menuTrigger='focus'
      inputValue={inputValue}
      onInputChange={setInputValue}
      selectedKey={null}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      aria-label='Search objects'
      className={className}
    >
      <AriaGroup
        ref={placeholderRef as RefObject<HTMLDivElement>}
        className={({
          isFocusWithin,
          isDisabled,
        }: {
          isFocusWithin: boolean;
          isDisabled: boolean;
        }) =>
          cx(
            'relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset',
            'px-3 py-2',
            isDisabled && 'cursor-not-allowed bg-disabled_subtle',
            isFocusWithin && 'ring-2 ring-brand'
          )
        }
      >
        {({ isDisabled }: { isDisabled: boolean }) => (
          <>
            <SearchLg className='pointer-events-none size-5 shrink-0 text-fg-quaternary' />
            <FocusScope contain={false} autoFocus={false} restoreFocus={false}>
              <InnerTypeahead
                selectedItems={selectedItems}
                onRemoveItem={handleRemoveItem}
                onInputChange={setInputValue}
                onKeyDown={handleKeyDown}
                inputValue={inputValue}
                placeholder={placeholder}
                disabled={isDisabled}
                multiple={multiple}
              />
            </FocusScope>
          </>
        )}
      </AriaGroup>

      <Popover
        size='md'
        triggerRef={placeholderRef}
        style={popoverStyle}
        className='max-h-72!'
      >
        <AriaListBox
          selectionMode='single'
          className='size-full outline-hidden'
        >
          {/* Caller-provided extra options (e.g. proposed entities) */}
          {showExtras && (
            <AriaSection>
              <AriaHeader className='px-3 py-1.5 text-caption text-tertiary'>
                {extraOptionsLabel}
              </AriaHeader>
              {visibleExtras.map(entity => (
                <AriaListBoxItem
                  key={`${entity.type}:${entity.id}`}
                  id={`${entity.type}:${entity.id}`}
                  textValue={entity.name}
                  className='px-1.5 py-px outline-hidden'
                >
                  {({
                    isFocused,
                    isSelected,
                  }: {
                    isFocused: boolean;
                    isSelected: boolean;
                  }) => (
                    <EntityOptionRow
                      entity={entity}
                      isFocused={isFocused}
                      isSelected={isSelected}
                      badge={
                        <Badge type='pill-color' color='brand' size='sm'>
                          New
                        </Badge>
                      }
                    />
                  )}
                </AriaListBoxItem>
              ))}
            </AriaSection>
          )}

          {/* Query too short state */}
          {showQueryTooShort && (
            <AriaListBoxItem
              id='query-too-short'
              textValue='Type at least 2 characters'
              className='px-3 py-4 text-center'
              isDisabled
            >
              <span className='text-sm text-tertiary'>
                Type at least 2 characters to search
              </span>
            </AriaListBoxItem>
          )}

          {/* Loading state */}
          {showLoading && (
            <AriaListBoxItem
              id='loading'
              textValue='Searching'
              className='flex items-center justify-center px-3 py-4'
              isDisabled
            >
              <LoadingIndicator size='xs' label='Searching…' />
            </AriaListBoxItem>
          )}

          {/* Error state */}
          {showError && (
            <AriaListBoxItem
              id='error'
              textValue='Error'
              className='px-3 py-4'
              isDisabled
            >
              <div className='flex flex-col items-center gap-2 text-center'>
                <AlertCircle className='size-8 text-fg-error-secondary' />
                <span className='text-sm text-tertiary'>
                  {error?.message || 'An error occurred'}
                </span>
                <button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    refetch();
                  }}
                  className='flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-brand-primary hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-focus-ring'
                >
                  <RefreshCw04 className='size-4' />
                  Retry
                </button>
              </div>
            </AriaListBoxItem>
          )}

          {/* No results state */}
          {showNoResults && (
            <AriaListBoxItem
              id='no-results'
              textValue='No results'
              className='px-3 py-4'
              isDisabled
            >
              <div className='flex flex-col items-center gap-2 text-center'>
                <SearchLg className='size-8 text-fg-quaternary' />
                <span className='text-sm text-tertiary'>
                  No results for &apos;{inputValue}&apos;
                </span>
              </div>
            </AriaListBoxItem>
          )}

          {/* Empty recents state (first-time users) */}
          {showEmptyRecents && (
            <AriaListBoxItem
              id='empty-recents'
              textValue='Search hint'
              className='px-3 py-4 text-center'
              isDisabled
            >
              <span className='text-sm text-tertiary'>
                Search users, contacts, companies, and deals
              </span>
            </AriaListBoxItem>
          )}

          {/* Recent searches */}
          {showRecents && (
            <AriaSection>
              <AriaHeader className='px-3 py-1.5 text-caption text-tertiary'>
                Recent
              </AriaHeader>
              {recentSearches.map(entity => (
                <AriaListBoxItem
                  key={`${entity.type}:${entity.id}`}
                  id={`${entity.type}:${entity.id}`}
                  textValue={entity.name}
                  className='px-1.5 py-px outline-hidden'
                >
                  {({
                    isFocused,
                    isSelected,
                  }: {
                    isFocused: boolean;
                    isSelected: boolean;
                  }) => (
                    <EntityOptionRow
                      entity={entity}
                      isFocused={isFocused}
                      isSelected={isSelected}
                    />
                  )}
                </AriaListBoxItem>
              ))}
            </AriaSection>
          )}

          {/* Search results grouped by type */}
          {showResults &&
            activeSections.map(({ type, items }) => (
              <AriaSection key={type}>
                <AriaHeader className='px-3 py-1.5 text-caption text-tertiary'>
                  {getSectionLabel(type)}
                </AriaHeader>
                {items.map(entity => (
                  <AriaListBoxItem
                    key={`${entity.type}:${entity.id}`}
                    id={`${entity.type}:${entity.id}`}
                    textValue={entity.name}
                    className='px-1.5 py-px outline-hidden'
                  >
                    {({
                      isFocused,
                      isSelected,
                    }: {
                      isFocused: boolean;
                      isSelected: boolean;
                    }) => (
                      <EntityOptionRow
                        entity={entity}
                        isFocused={isFocused}
                        isSelected={isSelected}
                      />
                    )}
                  </AriaListBoxItem>
                ))}
              </AriaSection>
            ))}
        </AriaListBox>
      </Popover>
    </AriaComboBox>
  );
}

export default EntityTypeahead;
